import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Star, TrendingUp, Users } from "lucide-react";
import { format } from "date-fns";

export function SatisfactionSurveyManager() {
  const { data: surveys, isLoading } = useQuery({
    queryKey: ["satisfaction_surveys_admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("connection_satisfaction_surveys")
        .select(`
          id,
          satisfaction_rating,
          created_at,
          user_id,
          connection_id
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch user profiles and connection details
      const userIds = [...new Set(data?.map((s) => s.user_id) || [])];
      const connectionIds = [...new Set(data?.map((s) => s.connection_id) || [])];

      let profiles: any[] = [];
      let connections: any[] = [];

      if (userIds.length > 0) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url, role")
          .in("id", userIds);
        profiles = profileData || [];
      }

      if (connectionIds.length > 0) {
        // Fetch connection details and resolve profiles for both sides
        const { data: connectionData } = await supabase
          .from("connections")
          .select("id, requester_id, recipient_id")
          .in("id", connectionIds);

        if (connectionData && connectionData.length > 0) {
          // Gather all user IDs from connections
          const connUserIds = [...new Set(connectionData.flatMap(c => [c.requester_id, c.recipient_id]))];
          const { data: connProfiles } = await supabase
            .from("profiles")
            .select("id, full_name, avatar_url")
            .in("id", connUserIds);

          const profileMap = new Map(connProfiles?.map(p => [p.id, p]) || []);

          connections = connectionData.map(c => ({
            id: c.id,
            requester: profileMap.get(c.requester_id) || { id: c.requester_id, full_name: "Unknown", avatar_url: null },
            recipient: profileMap.get(c.recipient_id) || { id: c.recipient_id, full_name: "Unknown", avatar_url: null },
          }));
        }
      }

      return data?.map((survey) => {
        const user = profiles.find((p) => p.id === survey.user_id);
        const connection = connections.find((c: any) => c.id === survey.connection_id);
        
        // Determine the other party in the connection
        let otherParty = null;
        if (connection) {
          if (survey.user_id === (connection as any).requester?.id) {
            otherParty = (connection as any).recipient;
          } else {
            otherParty = (connection as any).requester;
          }
        }

        return {
          ...survey,
          user,
          connection,
          otherParty,
        };
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalSurveys = surveys?.length || 0;
  const averageRating = totalSurveys > 0
    ? (surveys?.reduce((sum, s) => sum + s.satisfaction_rating, 0) || 0) / totalSurveys
    : 0;

  // Calculate rating distribution
  const ratingDistribution = {
    low: surveys?.filter((s) => s.satisfaction_rating >= 1 && s.satisfaction_rating <= 3).length || 0,
    medium: surveys?.filter((s) => s.satisfaction_rating >= 4 && s.satisfaction_rating <= 5).length || 0,
    high: surveys?.filter((s) => s.satisfaction_rating >= 6 && s.satisfaction_rating <= 7).length || 0,
  };

  const getRatingColor = (rating: number) => {
    if (rating <= 3) return "bg-destructive text-destructive-foreground";
    if (rating <= 5) return "bg-yellow-500 text-white";
    return "bg-green-500 text-white";
  };

  const getRatingLabel = (rating: number) => {
    if (rating <= 3) return "Low";
    if (rating <= 5) return "Medium";
    return "High";
  };

  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Surveys</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSurveys}</div>
            <p className="text-xs text-muted-foreground">Responses collected</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageRating.toFixed(1)}</div>
            <p className="text-xs text-muted-foreground">Out of 7</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rating Distribution</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">
                Low: {ratingDistribution.low}
              </Badge>
              <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
                Med: {ratingDistribution.medium}
              </Badge>
              <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                High: {ratingDistribution.high}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Survey Responses Table */}
      <Card>
        <CardHeader>
          <CardTitle>Survey Responses</CardTitle>
          <CardDescription>All satisfaction survey responses from completed connections</CardDescription>
        </CardHeader>
        <CardContent>
          {!surveys || surveys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No satisfaction surveys have been submitted yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Submitted By</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Connection Partner</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {surveys.map((survey) => (
                  <TableRow key={survey.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={survey.user?.avatar_url} />
                          <AvatarFallback>
                            {survey.user?.full_name?.charAt(0) || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{survey.user?.full_name || "Unknown"}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {survey.user?.role || "Unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {survey.otherParty ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={(survey.otherParty as any)?.avatar_url} />
                            <AvatarFallback>
                              {(survey.otherParty as any)?.full_name?.charAt(0) || "?"}
                            </AvatarFallback>
                          </Avatar>
                          <span>{(survey.otherParty as any)?.full_name || "Unknown"}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Unknown</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getRatingColor(survey.satisfaction_rating)}>
                        {survey.satisfaction_rating}/7 ({getRatingLabel(survey.satisfaction_rating)})
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(survey.created_at), "MMM d, yyyy 'at' h:mm a")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
